/**
 * Notification tone storage + lookup.
 *
 * Two layers of persistence:
 *
 * 1. **localStorage** (small, fast, sync): user's per-category tone choice.
 *    A simple `{ [category]: { kind: 'builtin' | 'custom' | 'silent',
 *    builtinId?: string, customId?: string } }` map. Loaded synchronously
 *    on first render so the right tone is ready the moment a notification
 *    arrives.
 *
 * 2. **IndexedDB** (large, async): the actual uploaded audio blobs. We
 *    deliberately don't put them in localStorage because audio files can
 *    easily exceed the 5MB-per-origin limit. IndexedDB is async but we
 *    eagerly preload referenced blobs into an in-memory cache on app boot
 *    so playback latency stays at HTMLAudioElement levels.
 *
 * Why per-device (no backend sync): ringtone preferences are personal and
 * device-specific (you might want a quieter chime on a shared laptop).
 * Adding a server endpoint + file storage would also pull in upload-size,
 * MIME-validation, and authn concerns we don't need for what is, today, a
 * UX nicety. If we ever want cross-device sync, the prefs/blobs can be
 * replicated to the existing user-settings endpoints without changing the
 * shape exposed here.
 */

const LS_KEY = 'treppan_notification_tones_v1';
const DB_NAME = 'treppan_notif_tones';
const DB_STORE = 'tones';

// ============ Notification categories shown in the settings UI ============
// Each category groups a set of backend notification `type` / `related_type`
// values so the user picks one tone for the whole bucket. Keeping the list
// short (six categories) makes the settings page approachable; mapping
// individual backend types to categories happens in `categoryForType`.
export const NOTIFICATION_CATEGORIES = [
  {
    key: 'comments',
    label: 'Comments & Feedback',
    description: 'CEO/HOD comments, agent feedback, voice notes',
    defaultBuiltin: 'chime',
  },
  {
    key: 'new_leads',
    label: 'New Leads',
    description: 'Lead assignments, new contacts',
    defaultBuiltin: 'arpeggio',
  },
  {
    key: 'reports',
    label: 'Daily Reports',
    description: 'Agent daily reports, executive briefs',
    defaultBuiltin: 'double_chime',
  },
  {
    key: 'follow_ups',
    label: 'Follow-ups & Tasks',
    description: 'Task reminders, follow-up due, lead inactive',
    defaultBuiltin: 'single_beep',
  },
  {
    key: 'meetings',
    label: 'Meetings',
    description: 'Meeting invites, reminders, cancellations',
    defaultBuiltin: 'chime',
  },
  {
    key: 'messages',
    label: 'Messages',
    description: 'Email, WhatsApp, chat messages',
    defaultBuiltin: 'single_beep',
  },
];

// ============ Built-in tones available in the picker ============
// These identifiers are passed through to NotificationSoundContext, which
// has the oscillator code for each. We keep the friendly name + description
// here so the UI doesn't have to know about the oscillator implementation.
export const BUILTIN_TONES = [
  { id: 'chime',        label: 'Chime',          description: 'Three-note ascending chord' },
  { id: 'arpeggio',     label: 'Arpeggio',       description: 'Bright four-note arpeggio with second burst' },
  { id: 'double_chime', label: 'Double Chime',   description: 'Three-note chord with reinforcing echo' },
  { id: 'single_beep',  label: 'Single Beep',    description: 'One quick sine-wave beep' },
];

// ============ Type → category mapping ============
// Backend emits a bunch of notification `type` strings; the context calls
// `categoryForType(type)` to pick the right tone bucket. New backend types
// just need one row here. This map mirrors what notification_service.py
// and push_notification_service.py actually emit — if a new
// notification_type lands without an entry, `categoryForType` logs a
// warning and falls back to follow_ups (the "needs attention" bucket),
// which is a safer default than comments because most unmapped events
// are alerts, not chat.
const TYPE_TO_CATEGORY = {
  // ---- Comments & Feedback ----
  comment: 'comments',
  new_comment: 'comments',
  note: 'comments',
  feedback_received: 'comments',
  feedback_reply: 'comments',
  voice_message: 'comments',
  voice_message_received: 'comments',
  user_mentioned: 'comments',
  ceo_comment: 'comments',
  brief: 'comments',

  // ---- New Leads ----
  contact_created: 'new_leads',
  duplicate_lead: 'new_leads',
  high_priority_lead: 'new_leads',
  new_lead_received: 'new_leads',
  new_lead: 'new_leads',
  owner_assigned: 'new_leads',
  owner_removed: 'new_leads',
  lead_reopened: 'new_leads',

  // ---- Daily Reports ----
  report: 'reports',
  ai_report_generated: 'reports',

  // ---- Follow-ups & Tasks (anything that needs an agent to act) ----
  // Backend uses BOTH `lead_inactivity` (long form) and prior code had
  // `lead_inactive` variants — list every spelling we've seen so the
  // mapping is forward-compatible.
  lead_inactivity: 'follow_ups',
  lead_inactive: 'follow_ups',
  lead_inactive_3d: 'follow_ups',
  task_due: 'follow_ups',
  task_assigned: 'follow_ups',
  // The follow-up reminder family — backend uses both `followup_*`
  // (no underscore between "follow" and "up") and `follow_up_reminder`
  // in different places.
  follow_up_reminder: 'follow_ups',
  followup_due: 'follow_ups',
  followup_snoozed: 'follow_ups',
  followup_escalated: 'follow_ups',
  followup_upcoming: 'follow_ups',
  followup_pending: 'follow_ups',
  followup_reminder: 'follow_ups',
  // Approvals are functionally follow-ups — they're a manager waiting on
  // an action from someone, with the same urgency curve.
  approval_requested: 'follow_ups',
  approval_approved: 'follow_ups',
  approval_rejected: 'follow_ups',
  // Deal lifecycle events also belong here — they prompt the agent to
  // do something next (call, advance, salvage, celebrate, etc.).
  deal_stage_changed: 'follow_ups',
  deal_won: 'follow_ups',
  deal_lost: 'follow_ups',
  deal_stuck: 'follow_ups',
  payment_overdue: 'follow_ups',
  status_change: 'follow_ups',
  ai_sentiment_alert: 'follow_ups',
  // System-level alerts about workflows / accounts share the same
  // attention-required tone profile.
  workflow_failed: 'follow_ups',
  system_alert: 'follow_ups',
  login_new_device: 'follow_ups',

  // ---- Meetings ----
  meeting_reminder: 'meetings',
  meeting_invited: 'meetings',
  meeting_scheduled: 'meetings',
  meeting_cancelled: 'meetings',

  // ---- Messages ----
  email_received: 'messages',
  whatsapp_received: 'messages',
  new_message: 'messages',
  missed_call: 'messages',
  broadcast: 'messages',
};

export function categoryForType(type) {
  if (!type) return 'follow_ups';
  const cat = TYPE_TO_CATEGORY[type];
  if (cat) return cat;
  // Surface the gap so we can fix the mapping when new backend types
  // ship. Default falls to follow_ups (attention-required) rather than
  // comments — that's the right "I don't know what this is, but it's
  // probably an alert" bucket.
  // eslint-disable-next-line no-console
  console.warn(`[notificationTones] Unmapped notification type "${type}" — defaulting to follow_ups. Add it to TYPE_TO_CATEGORY in notificationTones.js to silence.`);
  return 'follow_ups';
}

// ============ localStorage prefs ============
// Shape: { comments: { kind: 'builtin', builtinId: 'chime' },
//          new_leads: { kind: 'custom', customId: 'abc-123' },
//          follow_ups: { kind: 'silent' }, ... }

function defaultPrefs() {
  const out = {};
  for (const cat of NOTIFICATION_CATEGORIES) {
    out[cat.key] = { kind: 'builtin', builtinId: cat.defaultBuiltin };
  }
  return out;
}

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaultPrefs();
    const parsed = JSON.parse(raw);
    // Merge with defaults so newly-added categories fall back gracefully
    // without the user having to re-save the whole settings page.
    return { ...defaultPrefs(), ...parsed };
  } catch {
    return defaultPrefs();
  }
}

export function savePrefs(prefs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
    // Let any listener (e.g. the active NotificationSoundProvider instance)
    // know the prefs changed so it can swap its in-memory cache without
    // waiting for a full page reload.
    window.dispatchEvent(new CustomEvent('notification-tones-changed'));
  } catch (e) {
    console.warn('Failed to save notification tone prefs:', e);
  }
}

export function setCategoryPref(category, pref) {
  const prefs = loadPrefs();
  prefs[category] = pref;
  savePrefs(prefs);
}

// ============ IndexedDB for custom audio blobs ============
// Open the DB lazily. Each blob is keyed by a uuid we generated when the
// user uploaded the file; the localStorage pref stores that uuid as
// `customId`. Loading a tone is two-step: read the pref (sync) → look up
// the blob (async).

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export async function saveCustomTone(id, blob, filename) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put({ blob, filename, savedAt: Date.now() }, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getCustomTone(id) {
  if (!id) return null;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteCustomTone(id) {
  if (!id) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listCustomTones() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const store = tx.objectStore(DB_STORE);
    const items = [];
    const req = store.openCursor();
    req.onsuccess = (evt) => {
      const cursor = evt.target.result;
      if (cursor) {
        items.push({ id: cursor.key, filename: cursor.value.filename, savedAt: cursor.value.savedAt });
        cursor.continue();
      } else {
        resolve(items);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

// Convert an uploaded File into a stored tone. Returns the generated id so
// the caller can update the category pref to point at it.
export async function uploadCustomTone(file) {
  if (!file) throw new Error('No file provided');
  // Cap at 2MB — large enough for a few seconds of mp3, small enough to
  // keep IndexedDB tidy. Audio longer than ~5s is overkill for a chime.
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('File too large (max 2 MB)');
  }
  if (!/^audio\//.test(file.type)) {
    throw new Error('File must be an audio type (mp3, wav, ogg, m4a)');
  }
  const id = `tone_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await saveCustomTone(id, file, file.name);
  return { id, filename: file.name };
}

// Cache of object URLs by customId so we don't churn createObjectURL on
// every play. NotificationSoundProvider clears this on `tones-changed`.
const objectUrlCache = new Map();

export async function getCustomToneUrl(id) {
  if (objectUrlCache.has(id)) return objectUrlCache.get(id);
  const row = await getCustomTone(id);
  if (!row?.blob) return null;
  const url = URL.createObjectURL(row.blob);
  objectUrlCache.set(id, url);
  return url;
}

export function clearObjectUrlCache() {
  for (const url of objectUrlCache.values()) {
    try { URL.revokeObjectURL(url); } catch { /* ignore */ }
  }
  objectUrlCache.clear();
}
